import React, { useState, useCallback, useEffect, forwardRef } from "react";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import moment from "moment";
import { useDropzone } from "react-dropzone";
import {
  EditorState,
  ContentState,
  convertFromHTML,
  convertToRaw,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import { Editor } from "react-draft-wysiwyg";
import DatePicker from "react-datepicker";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import MuiFormControlLabel from "@mui/material/FormControlLabel";

import Close from "mdi-material-ui/Close";
import BottomResize from "mdi-material-ui/ResizeBottomRight";
import FileDocumentOutline from "mdi-material-ui/FileDocumentOutline";

import FormButtons from "views/components/formbuttons";
import FormHeader from "views/components/formheader";
import APICall from "network/APICall";
import EndPoints from "network/Endpoints";
import DefaultPalette from "@core/theme/palettes";
import { EditorOption } from "@core/layouts/utils";
import DatePickerWrapper from "@core/styles/react-datepicker";
import {
  isSpaceValidateEditor,
  isSpaceValidateTextField,
} from "@core/utils/Const";
import Loader from "@core/helper/Loader";
// import { date } from "yup";

const customTheme = DefaultPalette();

const FormControlLabel = styled(MuiFormControlLabel)(({ theme }) => ({
  "& .MuiFormControlLabel-label": {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

const Img = styled("img")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    marginRight: theme.spacing(5),
    marginLeft: theme.spacing(2),
  },
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    width: 200,
  },
}));

const HeadingTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(2),
  },
}));

const PickersComponent = forwardRef(({ ...props }, ref) => {
  const { label } = props;

  return <TextField inputRef={ref} {...props} label={label || ""} />;
});

const AddTodayProgram = (props) => {
  const { open, toggle, type, heading, onSubmitProgram, updateData } = props;
  const [updateDate, selectUpdateDate] = useState(null);
  const [updateEndDate, selectUpdateEndDate] = useState(null);
  const [files, setFiles] = useState([]);
  const [filename, setFilename] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [size, setSize] = useState({ y: 300 });
  const [width, setWidth] = useState(window.innerWidth);

  const handleWindowResize = () => {
    setWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    maxSize: 2000000,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    onDrop: (acceptedFiles) => {
      setFiles(acceptedFiles.map((file) => Object.assign(file)));
      setValue("image", acceptedFiles);
      setRemoveImage(false);
    },
    onDropRejected: () => {
      setRemoveImage(true);
      toast.error("You can only upload 1 file & maximum size of 2 MB.", {
        duration: 2000,
      });
    },
  });

  const renderFilePreview = (file) => {
    if (file.type.startsWith("image")) {
      return (
        <img
          width={38}
          height={38}
          alt={file.name}
          src={URL.createObjectURL(file)}
        />
      );
    } else {
      return <FileDocumentOutline />;
    }
  };

  const handleRemoveFile = () => {
    setFiles([]);
    setValue("image", "");
    setFilename("");
    setRemoveImage(true);
  };

  const onHandleDate = (date) => {
    const endDate = moment(date).add(30, "m").toDate();

    setValue("date", date);
    setValue("end_date", endDate);
    selectUpdateDate(date);
    clearErrors("date");
  };

  const onHandleEndDate = (date) => {
    const startMoment = moment(updateDate);
    const endMoment = moment(date);

    if (!endMoment.isAfter(startMoment)) {
      setError("end_date", {
        type: "manual",
        message: "Previous date is not valid",
      });
    } else {
      clearErrors("end_date");
    }
    setValue("end_date", date);
    selectUpdateEndDate(date);
  };

  const defaultValues = {
    title: "",
    description: "",
    content: "",
    image: "",
    date: null,
    end_date: null,
    notification: false,
  };

  const {
    setValue,
    reset,
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({ defaultValues });

  const resetToEmptyValues = useCallback(() => {
    setValue("title", "");
    setValue("description", "");
    setValue("image", "");
    setValue("date", "");
    setValue("end_date", "");
    setValue("content", "");
    setValue("notification", false);
    setFiles([]);
  }, [setValue]);

  const resetToStoredValues = useCallback(() => {
    if (type === "edit") {
      const event = {
        title: updateData?.title,
        description: updateData?.description,
        content: EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML(`${updateData?.content}`)
          )
        ),
        image: updateData.image,
      };

      const input = updateData.date;
      const input2 = updateData.end_date;
      const fmt = "MMM DD, YYYY HH:mm:ss";
      const m = moment.utc(input).format(fmt);
      const m2 = moment.utc(input2).format(fmt);
      selectUpdateDate(m);
      const fetchDate = new Date(m === undefined ? updateData?.date : m);
      const fetchDate2 =
        updateData?.end_date === null
          ? ""
          : new Date(m2 === undefined ? updateData?.end_date : m2);
      selectUpdateEndDate(fetchDate2);
      setValue("date", fetchDate);
      setValue("end_date", fetchDate2);
      setFilename(event.image || "");
      setValue("title", event.title || "");
      setValue("description", event.description || "");
      setValue("image", event.image || "");
      setValue("content", event.content || "");
      setValue("notification", false);
    }
  }, [
    setValue,
    type,
    updateData?.content,
    updateData?.date,
    updateData?.description,
    updateData?.end_date,
    updateData?.image,
    updateData?.title,
  ]);

  useEffect(() => {
    if (type === "edit") {
      resetToStoredValues();
    } else {
      resetToEmptyValues();
    }
  }, [open, resetToStoredValues, resetToEmptyValues, type]);

  const handleClose = () => {
    if (type === "add") {
      reset(defaultValues);
      setFiles([]);
    }
    setRemoveImage(false);
    toggle();
    setValue("date", null);
    setValue("end_date", null);
    selectUpdateDate(null);
    selectUpdateEndDate(null);
    setFiles([]);
  };

  const submitProgram = (data, selectImage) => {
    const startMoment = moment(data.date);
    const endMoment = moment(data.end_date);

    if (!endMoment.isAfter(startMoment)) {
      toast.error("End date and time must be after the start date and time");
      setError("end_date", {
        type: "manual",
        message: "End date and time must be after the start date and time",
      });
      return;
    }

    const startDateFormat = startMoment.format("MM/DD/yyyy HH:mm:ss");
    const endDateFormat = endMoment.format("MM/DD/yyyy HH:mm:ss");

    const payload = {
      title: data.title,
      content: draftToHtml(convertToRaw(data.content.getCurrentContent())),
      description: data.description,
      end_date: endDateFormat,
      date: startDateFormat,
      isNotification: data.notification,
    };
    if (type === "add") {
      const payloads = {
        ...payload,
        image: files.length > 0 ? selectImage : "",
      };
      Loader.isLoading(true);
      APICall("post", payloads, EndPoints.addProgram)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitProgram();
            handleClose();
          } else {
            toast.error(message);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          Loader.isLoading(false);
        });
    } else {
      const date1 = moment(data.date).isAfter(data.end_date);
      if (date1) {
        setError("end_date", {
          type: "manual",
          message: "Previous date is not valid",
        });
        return;
      }
      const payloads = {
        id: updateData.id,
        ...payload,
        image: !selectImage ? "" : selectImage,
        isRemoveImage: removeImage,
      };
      Loader.isLoading(true);
      APICall("post", payloads, EndPoints.updateProgram)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitProgram();
            handleClose();
          } else {
            toast.error(message);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          Loader.isLoading(false);
        });
    }
  };

  const onSubmit = (values) => {
    if (
      convertToRaw(values.content.getCurrentContent()).blocks.length === 1 &&
      convertToRaw(values.content.getCurrentContent()).blocks[0].text === ""
    ) {
      setValue("content", "");
      setError("content");
      return;
    }

    if (!values.image || files.length === 0) {
      submitProgram(values, "");
      return;
    }
    Loader.isLoading(true);
    const formData = new FormData();
    formData.append("image", values.image[0]);
    formData.append("type", "today_program");

    APICall("post", formData, EndPoints.uploadImage)
      .then((response) => {
        const { isError, data, message } = response.data;
        if (!isError) {
          submitProgram(values, data.fileName);
        } else {
          toast.error(message);
        }
      })
      .catch((error) => {
        return error;
      })
      .finally(() => {
        Loader.isLoading(false);
      });
  };

  const fileList = files.map((file) => (
    <ListItem
      key={file.name}
      sx={{
        justifyContent: "space-between",
        display: "flex",
      }}
    >
      <div
        className="file-details"
        style={{ display: "flex", alignItems: "center", width: "90%" }}
      >
        <div className="file-preview">{renderFilePreview(file)}</div>
        <div style={{ marginLeft: 2, flexDirection: "column", width: "90%" }}>
          <Typography
            className="file-name"
            variant="body2"
            sx={{ wordWrap: "break-word" }}
          >
            {file.name}
          </Typography>
          <Typography className="file-size" variant="body2">
            {Math.round(file.size / 100) / 10 > 1000
              ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} mb`
              : `${(Math.round(file.size / 100) / 10).toFixed(1)} kb`}
          </Typography>
        </div>
      </div>
      <IconButton onClick={() => handleRemoveFile()}>
        <Close fontSize="small" />
      </IconButton>
    </ListItem>
  ));

  const handler = (mouseDownEvent) => {
    const startSize = size;
    const startPosition = { y: mouseDownEvent.pageY };

    function onMouseMove(mouseMoveEvent) {
      setSize(() => ({
        y: startSize.y - startPosition.y + mouseMoveEvent.pageY,
      }));
    }
    function onMouseUp() {
      document.body.removeEventListener("mousemove", onMouseMove);
    }

    document.body.addEventListener("mousemove", onMouseMove);
    document.body.addEventListener("mouseup", onMouseUp, { once: true });
  };

  const maxSize = width < 600 ? 230 : 150;

  return (
    <Drawer
      open={open}
      anchor="right"
      variant="temporary"
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { width: { xs: 400, sm: 600 } } }}
    >
      <FormHeader heading={heading} onClick={handleClose} />
      <Box sx={{ p: 5 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: true, validate: isSpaceValidateTextField }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Title"
                      placeholder="Enter title"
                      error={Boolean(errors.title)}
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.title && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    required: false
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      aria-describedby="validation-basic-first-name"
                      multiline
                      label="Description"
                      placeholder="Description"
                    />
                  )}
                />
                {errors.description && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Card
                sx={{
                  "& .MuiCardHeader-action": { lineHeight: 0.6 },
                  overflow: "visible",
                  position: "relative",
                }}
                id={"card-snippet--Description"}
              >
                <CardHeader
                  title={"Content"}
                  titleTypographyProps={{
                    variant: "h6",
                    fontSize: "1rem !important",
                  }}
                />
                <CardContent
                  sx={{
                    minHeight: maxSize,
                    height: size.y,
                    p: 3,
                  }}
                >
                  <FormControl fullWidth>
                    <Controller
                      name="content"
                      control={control}
                      rules={{
                        required: true,
                        validate: isSpaceValidateEditor,
                      }}
                      render={({ field: { value, onChange } }) => (
                        <Editor
                          editorState={value}
                          editorStyle={{
                            paddingTop: 1,
                            height: size.y,
                            maxHeight: size.y - maxSize,
                          }}
                          stripPastedStyles={true}
                          wrapperStyle={{ textDecoration: "none" }}
                          onEditorStateChange={onChange}
                          error={Boolean(errors.content)}
                          aria-describedby="validation-basic-first-name"
                          toolbar={{
                            options: EditorOption,
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                    p: 2,
                    cursor: "pointer",
                  }}
                  onMouseDown={handler}
                >
                  <BottomResize fontSize="small" />
                </Box>
                <Box
                  sx={{
                    height: 5,
                    display: "flex",
                    cursor: "n-resize",
                  }}
                  onMouseDown={handler}
                ></Box>
              </Card>
              {errors.content && (
                <FormHelperText
                  sx={{ color: "error.main", ml: 3, mt: 1 }}
                  id="validation-basic-first-name"
                >
                 This field is required
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12}>
              {type === "edit" && files.length === 0 && filename ? (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderWidth: 1,
                      borderStyle: "dashed",
                      borderColor: customTheme.grey.A200,
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 2,
                      display: "flex",
                      cursor: "pointer",
                    }}
                  >
                    <Img width={100} alt="Upload img" src={updateData.image} />
                    <IconButton onClick={() => handleRemoveFile()}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ) : files.length > 0 ? (
                <div>
                  <Box
                    sx={{
                      borderWidth: 1,
                      borderRadius: 1,
                      borderStyle: "dashed",
                      borderColor: customTheme.grey.A200,
                    }}
                  >
                    <List>{fileList}</List>
                  </Box>
                </div>
              ) : (
                <FormControl fullWidth>
                  <Controller
                    name="image"
                    control={control}
                    rules={{ required: false }}
                    render={() => (
                      <div {...getRootProps({ className: "dropzone" })}>
                        <input {...getInputProps()} />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: ["column", "column", "row"],
                            alignItems: "center",
                            borderWidth: 1,
                            borderRadius: 1,
                            borderStyle: "dashed",
                            borderColor: customTheme.grey.A200,
                          }}
                        >
                          <Img
                            width={100}
                            alt="Upload img"
                            src="/images/misc/upload.png"
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              textAlign: ["center", "center", "inherit"],
                            }}
                          >
                            <HeadingTypography>
                              Drop file here or click to upload.
                            </HeadingTypography>
                            <Typography color="textSecondary" variant="body2">
                              Allowed *.jpeg, *.jpg, *.png
                            </Typography>
                            <Typography color="textSecondary" variant="body2">
                              Max 1 file and max size of 2 MB
                            </Typography>
                          </Box>
                        </Box>
                      </div>
                    )}
                  />
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12}>
              <DatePickerWrapper>
                <FormControl fullWidth>
                  <Box sx={{ display: "flex", width: "100%" }}>
                    <Controller
                      name="date"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { value } }) => (
                        <>
                          <DatePicker
                            selected={
                              !updateEndDate
                                ? value
                                : new Date(
                                    updateDate === undefined
                                      ? updateData?.date
                                      : updateDate
                                  )
                            }
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={1}
                            id="date-time-picker"
                            timeCaption="Time Picker"
                            dateFormat="dd/MM/yyyy h:mm aa"
                            onChange={onHandleDate}
                            error={Boolean(errors.date)}
                            customInput={
                              <PickersComponent label="Start Date" />
                            }
                            autoComplete={"off"}
                            onKeyDown={(e) => e.preventDefault()} // block typing & arrow keys
                            onPaste={(e) => e.preventDefault()}
                          />
                        </>
                      )}
                    />
                  </Box>
                </FormControl>
                {errors.date && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </DatePickerWrapper>
            </Grid>

            <Grid item xs={12}>
              <DatePickerWrapper>
                <FormControl fullWidth>
                  <Box sx={{ display: "flex", width: "100%" }}>
                    <Controller
                      name="end_date"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { value } }) => (
                        <>
                          <DatePicker
                            selected={
                              !updateEndDate ? value : new Date(updateEndDate)
                            }
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={30}
                            id="date-time-picker"
                            timeCaption="Time Picker"
                            dateFormat="dd/MM/yyyy h:mm aa"
                            minDate={updateDate}
                            onChange={onHandleEndDate}
                            error={Boolean(errors.end_date)}
                            customInput={<PickersComponent label="End Date" />}
                            disabled={!updateDate}
                            autoComplete={"off"}
                            onKeyDown={(e) => e.preventDefault()} // block typing & arrow keys
                            onPaste={(e) => e.preventDefault()}
                          />
                        </>
                      )}
                    />
                  </Box>
                </FormControl>

                {errors.end_date && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    {!errors.end_date.message
                      ? "This field is requied"
                      : errors.end_date.message}
                  </FormHelperText>
                )}
              </DatePickerWrapper>
            </Grid>

            <Grid item xs={12} sx={{ paddingLeft: "1rem" }}>
              <FormControl fullWidth>
                <Controller
                  name="notification"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <FormControlLabel
                        control={
                          <Checkbox checked={value} onChange={onChange} />
                        }
                        label={
                          <Typography variant="body1" component="span">
                            Send Notification
                          </Typography>
                        }
                      />
                    );
                  }}
                />
              </FormControl>
            </Grid>

            <FormButtons type={type} resetFunction={resetToEmptyValues} />
          </Grid>
        </form>
      </Box>
    </Drawer>
  );
};

export default AddTodayProgram;
